import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import AssistantComposer from './AssistantComposer';
import AssistantMessage from './AssistantMessage';
import { RotateCw } from 'lucide-react';
import UserMessage from './UserMessage';
import WelcomeScreen from './WelcomeScreen';
import { mixpanelTrack } from 'utils/app/configureMixpanel';
import { useAssistantContext } from './AssistantProvider';
import { useAuth0 } from '@auth0/auth0-react';

function getErrorMessage(error) {
    const raw = error?.message || String(error);
    try {
        const parsed = JSON.parse(raw);
        return parsed.error || raw;
    } catch {
        return raw;
    }
}

function getOffsetTopWithin(el, container) {
    return (
        el.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop
    );
}

function scrollElementTopToPaneTop(container, el, behavior) {
    container.scrollTo({
        top: Math.max(0, getOffsetTopWithin(el, container)),
        behavior,
    });
}

function scrollElementBottomToPaneBottom(container, el, behavior) {
    const top =
        getOffsetTopWithin(el, container) +
        el.offsetHeight -
        container.clientHeight;
    container.scrollTo({ top: Math.max(0, top), behavior });
}

function getLastAssistantEl(container) {
    const all = container.querySelectorAll('[data-message-role="assistant"]');
    return all[all.length - 1] || null;
}

function getLastMessageEl(container) {
    const all = container.querySelectorAll('[data-message-role]');
    return all[all.length - 1] || null;
}

const AssistantChat = () => {
    const [input, setInput] = useState('');
    const scrollContainerRef = useRef(null);
    const spacerRef = useRef(null);
    const textareaRef = useRef(null);
    const prevMessagesLenRef = useRef(0);
    const prevContentRef = useRef('');
    const prevChatIdRef = useRef(null);
    const pendingChatLoadRef = useRef(false);

    const { user } = useAuth0();
    const firstName =
        user?.given_name ||
        user?.nickname ||
        user?.name?.split(' ')[0] ||
        'there';

    const {
        isLoading,
        currentContent,
        messages,
        error,
        sendMessage,
        cancelStream,
        activeChatId,
        modelTier,
        setModelTier,
    } = useAssistantContext();

    useEffect(() => {
        const prevLen = prevMessagesLenRef.current;
        const prevContent = prevContentRef.current;
        const prevChatId = prevChatIdRef.current;

        prevMessagesLenRef.current = messages.length;
        prevContentRef.current = currentContent;
        prevChatIdRef.current = activeChatId;

        const container = scrollContainerRef.current;
        if (!container) return;

        const isChatSwitch = prevChatId !== activeChatId;
        if (isChatSwitch) {
            pendingChatLoadRef.current = true;
        }

        if (pendingChatLoadRef.current && messages.length > 0) {
            pendingChatLoadRef.current = false;
            const target =
                getLastAssistantEl(container) || getLastMessageEl(container);
            if (target) {
                scrollElementBottomToPaneBottom(container, target, 'auto');
            }
            return;
        }

        if (pendingChatLoadRef.current) return;

        const newMessageAdded = messages.length > prevLen;
        const streamingStarted = !prevContent && currentContent;

        if (newMessageAdded) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage?.role === 'user') {
                const target = getLastMessageEl(container);
                if (target) {
                    scrollElementTopToPaneTop(container, target, 'smooth');
                }
                return;
            }
            // New assistant message: don't scroll yet. If text is already
            // present (no preceding tool calls), streamingStarted will fire
            // below in this same pass. Otherwise, leave the user message
            // anchored at the top while steps stream in.
        }

        if (streamingStarted) {
            const target = getLastAssistantEl(container);
            if (target) scrollElementTopToPaneTop(container, target, 'smooth');
        }
    }, [messages, currentContent, isLoading, activeChatId]);

    const isEmpty = messages.length === 0 && !currentContent;

    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        const spacer = spacerRef.current;
        if (!container || !spacer) return;

        const update = () => {
            const lastMessage = getLastMessageEl(container);
            if (!lastMessage) {
                spacer.style.minHeight = '0px';
                return;
            }
            // Pick spacer height so that scrolling to top-align the last
            // message exhausts the scroll range exactly: max scrollTop ===
            // lastMessage offset. This both enables top-alignment AND
            // prevents the user from scrolling messages out of view.
            const lastMessageTop = getOffsetTopWithin(lastMessage, container);
            const contentWithoutSpacer =
                container.scrollHeight - spacer.offsetHeight;
            const required =
                lastMessageTop + container.clientHeight - contentWithoutSpacer;
            spacer.style.minHeight = `${Math.max(0, required)}px`;
        };

        update();

        const observer = new ResizeObserver(update);
        observer.observe(container);
        const lastMessage = getLastMessageEl(container);
        if (lastMessage) observer.observe(lastMessage);
        return () => observer.disconnect();
    }, [isEmpty, messages.length, isLoading, error]);

    useEffect(() => {
        textareaRef.current?.focus();
    }, [activeChatId]);

    const resetTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    };

    const handleSubmit = (e) => {
        e?.preventDefault();
        if (!input.trim()) return;
        pendingChatLoadRef.current = false;
        if (isLoading) cancelStream();
        sendMessage(input.trim());
        setInput('');
        resetTextareaHeight();
    };

    const handleUseCaseSelect = (item) => {
        mixpanelTrack('AI Assistant Suggestion Clicked', {
            suggestion_text: item.prompt,
            use_case: item.label,
            source: 'panel',
        });
        setInput(item.prompt);
        requestAnimationFrame(() => {
            const el = textareaRef.current;
            if (!el) return;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className="flex flex-col h-full bg-background">
            {/* Messages area */}
            <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 [overflow-anchor:none] overscroll-contain"
            >
                {isEmpty ? (
                    <WelcomeScreen
                        firstName={firstName}
                        onSelectUseCase={handleUseCaseSelect}
                    />
                ) : (
                    <>
                        {(() => {
                            const lastAssistantIndex = messages.findLastIndex(
                                (m) => m.role === 'assistant',
                            );
                            return messages.map((message, index) => {
                                if (message.role === 'user') {
                                    return (
                                        <UserMessage
                                            key={message.id || index}
                                            message={message}
                                        />
                                    );
                                }
                                if (message.role === 'assistant') {
                                    return (
                                        <AssistantMessage
                                            key={message.id || index}
                                            message={message}
                                            isLoading={
                                                isLoading &&
                                                index === messages.length - 1
                                            }
                                            isLastAssistantMessage={
                                                index === lastAssistantIndex
                                            }
                                        />
                                    );
                                }
                                console.error(
                                    `Unknown message role: ${message.role}`,
                                    message,
                                );
                                return null;
                            });
                        })()}

                        {!isLoading &&
                            messages.length > 0 &&
                            messages[messages.length - 1].role === 'user' && (
                                <button
                                    onClick={() => {
                                        const lastUserMsg =
                                            messages[messages.length - 1];
                                        const text = lastUserMsg.parts
                                            ?.filter((p) => p.type === 'text')
                                            .map((p) => p.text)
                                            .join('');
                                        if (text)
                                            sendMessage(text, {
                                                is_retry: true,
                                            });
                                    }}
                                    className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted rounded-lg transition-colors"
                                >
                                    <RotateCw size={12} />
                                    Retry
                                </button>
                            )}

                        {error && (
                            <div
                                role="alert"
                                className="px-3.5 py-2.5 bg-red-50 text-red-700 rounded-lg text-sm"
                            >
                                {getErrorMessage(error)}
                            </div>
                        )}

                        <div ref={spacerRef} aria-hidden="true" />
                    </>
                )}
            </div>

            {/* Input area */}
            <AssistantComposer
                value={input}
                onValueChange={setInput}
                onSubmit={handleSubmit}
                onKeyDown={handleKeyDown}
                isLoading={isLoading}
                modelTier={modelTier}
                setModelTier={setModelTier}
                textareaRef={textareaRef}
                showFreePreview
            />
        </div>
    );
};

export default AssistantChat;
